import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  ArrowRight,
  Home,
  Users,
  Sparkles,
  HeartHandshake,
  Trophy,
  ClipboardCheck,
  Compass,
  Quote,
  Mail,
  Phone,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1758687126877-b37052a20a4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHxmYW1pbHklMjBzdXBwb3J0aW5nJTIwY2hpbGQlMjBhdCUyMGhvbWV8ZW58MHx8fHwxNzg1NzIyNjUzfDA&ixlib=rb-4.1.0&q=85";
const BETWEEN_IMG =
  "https://images.unsplash.com/photo-1768244017009-ae72704195cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHw0fHxkaXNhYmxlZCUyMHBlcnNvbiUyMGluJTIwY29tbXVuaXR5JTIwc21pbGluZ3xlbnwwfHx8fDE3ODU3MjI2NTN8MA&ixlib=rb-4.1.0&q=85";
const WIN_IMG =
  "https://images.unsplash.com/photo-1713946598491-4f85decbeaaf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHx0aGVyYXB5JTIwY2VsZWJyYXRpb24lMjBoaWdoJTIwZml2ZXxlbnwwfHx8fDE3ODU3MjI2NTN8MA&ixlib=rb-4.1.0&q=85";

const services = [
  {
    icon: Compass,
    title: "Implementation coaching",
    body: "We translate therapy recommendations into everyday routines that families can actually run at home.",
  },
  {
    icon: Users,
    title: "Provider coordination",
    body: "One shared plan across therapists, support workers and school so everyone is rowing in the same direction.",
  },
  {
    icon: HeartHandshake,
    title: "Family capacity building",
    body: "Practical coaching for parents, carers and siblings — because the people at home are the real change-makers.",
  },
  {
    icon: Trophy,
    title: "Celebrating wins",
    body: "We log the small daily moments that prove capacity is being built — and share them with the whole team.",
  },
  {
    icon: ClipboardCheck,
    title: "Goal tracking that fits life",
    body: "NDIS goals, broken down into things you can practice this week. No jargon. No 40-page reports nobody reads.",
  },
  {
    icon: Home,
    title: "In the community, at home",
    body: "Support that shows up where life actually happens — the shops, the bus, the kitchen, the backyard.",
  },
];

const storyLines = [
  { text: "Participants had funding.", tone: "muted" },
  { text: "Participants had therapists.", tone: "muted" },
  { text: "Participants had support workers.", tone: "muted" },
  { text: "Participants had reports.", tone: "muted" },
  { text: "Participants had goals.", tone: "muted" },
  { text: "Yet many were still facing the same barriers years later.", tone: "loud" },
];

const questions = [
  {
    q: "What happens between appointments?",
    a: "We turn every therapy recommendation into a routine your family can actually run at home. This week, not one day. Real practice, in the moments when learning actually sticks.",
  },
  {
    q: "Who reinforces the goals?",
    a: "Your integrated team does. Support workers, family and school all working from the one plan, in plain language. Everyone knows the goal for this week and how their role connects to it.",
  },
  {
    q: "Who celebrates the wins?",
    a: "We log every small win the moment it happens and share it with the whole team. Momentum keeps building, and participants get to see just how much they are achieving.",
  },
  {
    q: "Who helps families understand the strategy?",
    a: "A dedicated implementation coach translates therapy plans into everyday moves parents and carers can practise. No jargon. Just clear steps you can actually run at the kitchen table.",
  },
  {
    q: "Who makes sure providers work towards the same outcome?",
    a: "One shared strategy, one place for notes. Therapists, coordinators and support workers finally row the same way. Fewer contradictions, more compound progress across the week.",
  },
  {
    q: "Who helps participants see what they are capable of?",
    a: "Weekly reflections, visible progress and celebrated wins remind participants exactly how far they have come. Capability grows when it is named, tracked and honoured out loud.",
  },
];

export default function Landing() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Thanks — Christine will be in touch soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-navy text-white">
      <Navbar variant="landing" />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 -z-10 grain-overlay">
          <div className="absolute -top-32 -left-24 h-[520px] w-[520px] rounded-full bg-brand-magenta/25 blur-3xl" />
          <div className="absolute -bottom-32 -right-24 h-[520px] w-[520px] rounded-full bg-brand-teal/25 blur-3xl" />
        </div>
        <div className="mx-auto max-w-7xl px-5 lg:px-10 pt-16 lg:pt-24 pb-20 lg:pb-32 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 stagger">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-white/80">
              <Sparkles className="h-3.5 w-3.5 text-brand-teal" />
              NDIS Implementation Support
            </div>
            <h1 className="mt-6 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Capacity isn't built in appointments.
              <br />
              <span className="arch-underline text-brand-teal">It's built every day.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-white/75 leading-relaxed">
              At home. In the community. In the small moments between the therapy sessions and the paperwork.
              That's where real change lives — and that's where we show up.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-cta-start">
                <Button className="rounded-full bg-brand-teal hover:bg-brand-tealDark text-white btn-lift px-6 h-12 text-base">
                  Start your journey <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href="#story" data-testid="hero-cta-story">
                <Button
                  variant="outline"
                  className="rounded-full bg-transparent border-white/25 text-white hover:bg-white/10 btn-lift px-6 h-12 text-base"
                >
                  Read the story
                </Button>
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
              <Stat number="7+" label="years supporting families" />
              <Stat number="100%" label="participant-led plans" />
              <Stat number="Every day" label="between appointments" />
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-brand-teal/30 to-brand-magenta/30 blur-2xl" />
              <img
                src={HERO_IMG}
                alt="A family supporting each other at home"
                className="relative rounded-[2rem] object-cover w-full h-[420px] lg:h-[520px] ring-1 ring-white/15 shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 hidden sm:block max-w-[240px] rounded-2xl bg-white text-brand-navy p-4 shadow-xl">
                <div className="flex items-center gap-2 text-brand-magenta font-semibold text-sm">
                  <Trophy className="h-4 w-4" /> Today's win
                </div>
                <p className="mt-1 text-sm text-brand-navy/80">Ordered coffee independently at the local café.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE STORY */}
      <section id="story" className="relative py-20 lg:py-32 bg-brand-navyDeep" data-testid="story-section">
        <div className="mx-auto max-w-5xl px-5 lg:px-10">
          <p className="text-xs uppercase tracking-[0.25em] text-brand-teal">The question that changed everything</p>
          <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl">
            If everyone is working so hard…
            <br />
            <span className="text-brand-magenta">why aren't we seeing more change?</span>
          </h2>

          <div className="mt-14 grid md:grid-cols-2 gap-10 lg:gap-16 items-start">
            <div className="space-y-3 stagger">
              {storyLines.map((l, i) => (
                <p
                  key={i}
                  className={`font-display text-xl sm:text-2xl leading-snug ${
                    l.tone === "loud" ? "text-white font-semibold" : "text-white/50"
                  }`}
                >
                  {l.text}
                </p>
              ))}
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-7 lg:p-9">
              <Quote className="h-8 w-8 text-brand-teal" />
              <p className="mt-4 text-lg lg:text-xl text-white/85 leading-relaxed">
                So I started asking a simple question. The answer wasn't more funding. Not more reports.
                Not more appointments.
              </p>
              <p className="mt-4 font-display text-2xl lg:text-3xl text-white font-bold tracking-tight">
                The answer was <span className="arch-underline text-brand-teal">implementation.</span>
              </p>
              <p className="mt-4 text-sm text-white/60 uppercase tracking-widest">— Christine Kaatz</p>
            </div>
          </div>

          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {questions.map((item, i) => (
              <div
                key={i}
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:bg-white/[0.05] transition-colors"
                data-testid={`story-question-${i}`}
              >
                <div className="text-brand-magenta font-display font-bold text-2xl">0{i + 1}</div>
                <p className="mt-3 text-white/90 font-display font-semibold text-lg leading-snug">{item.q}</p>
                <p className="mt-3 text-white/70 text-sm leading-relaxed">{item.a}</p>
                <p className="mt-4 text-[10px] uppercase tracking-widest text-brand-teal">Integrated support says: we do.</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BETWEEN APPOINTMENTS */}
      <section id="between" className="py-20 lg:py-32 bg-brand-navy" data-testid="between-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <img
              src={BETWEEN_IMG}
              alt="A person smiling out in the community"
              className="rounded-[2rem] object-cover w-full h-[420px] lg:h-[520px] ring-1 ring-white/10 shadow-2xl"
            />
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <p className="text-xs uppercase tracking-[0.25em] text-brand-magenta">Between appointments</p>
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight">
              People don't build capacity during appointments alone.
            </h2>
            <p className="mt-6 text-lg text-white/75 max-w-2xl leading-relaxed">
              Capacity is built every day. At home. In the community. In the small choices, the repetitions,
              the celebrations, and the coaching that happens in between formal sessions.
            </p>
            <ul className="mt-8 space-y-4 stagger">
              {[
                "Reinforce the goals that matter — every day of the week.",
                "Celebrate the small wins that add up to real change.",
                "Help families understand the strategy in plain language.",
                "Keep every provider working toward the same outcome.",
                "Help participants see what they are capable of — again and again.",
              ].map((t, i) => (
                <li key={i} className="flex gap-3 text-white/85">
                  <span className="mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-teal/20 text-brand-teal text-sm font-bold">
                    {i + 1}
                  </span>
                  <span className="text-base leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-20 lg:py-32 bg-brand-sand text-brand-navy" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.25em] text-brand-magenta">What we do</p>
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
              Practical NDIS support that sticks —
              <span className="arch-underline"> between the appointments.</span>
            </h2>
            <p className="mt-5 text-brand-mute text-lg leading-relaxed">
              Independent NDIS integrated support services designed around the way real families,
              real participants, and real providers actually work together.
            </p>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  className="group rounded-2xl bg-white p-7 border border-brand-navy/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_18px_50px_rgb(30,26,60,0.08)] transition-shadow"
                  data-testid={`service-card-${i}`}
                >
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-teal/10 text-brand-teal group-hover:bg-brand-teal group-hover:text-white transition-colors">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 font-display font-bold text-xl tracking-tight">{s.title}</h3>
                  <p className="mt-3 text-brand-mute text-[15px] leading-relaxed">{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WIN CTA */}
      <section className="py-20 lg:py-24 bg-brand-sand" data-testid="win-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <div className="relative overflow-hidden rounded-[2rem] bg-brand-navy p-8 sm:p-12 lg:p-16 grid lg:grid-cols-12 gap-10 items-center">
            <div className="absolute -top-20 -right-16 h-72 w-72 rounded-full bg-brand-magenta/25 blur-3xl" />
            <div className="absolute -bottom-20 -left-16 h-72 w-72 rounded-full bg-brand-teal/25 blur-3xl" />
            <div className="relative lg:col-span-7">
              <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-white leading-tight tracking-tight">
                Log a win. Track a goal. Share the strategy.
              </h2>
              <p className="mt-4 text-white/75 text-lg max-w-xl leading-relaxed">
                Your private participant space — for family, providers and the team to keep implementing
                the plan when the appointment finishes.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register" data-testid="win-cta-register">
                  <Button className="rounded-full bg-brand-teal hover:bg-brand-tealDark text-white btn-lift h-12 px-6 text-base">
                    Create a free account <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/login" data-testid="win-cta-login">
                  <Button variant="outline" className="rounded-full bg-transparent border-white/25 text-white hover:bg-white/10 btn-lift h-12 px-6 text-base">
                    Sign in
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative lg:col-span-5">
              <img
                src={WIN_IMG}
                alt="Celebrating a small daily win"
                className="rounded-2xl object-cover w-full h-[280px] lg:h-[340px] ring-1 ring-white/10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-20 lg:py-32 bg-brand-navy" data-testid="contact-section">
        <div className="mx-auto max-w-6xl px-5 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.25em] text-brand-teal">Get in touch</p>
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-tight">
              Let's talk about what happens between the appointments.
            </h2>
            <p className="mt-5 text-white/75 text-lg leading-relaxed">
              Every enquiry goes directly to Christine. No forms lost in an inbox, no long waits.
            </p>
            <div className="mt-8 space-y-4 text-white/85">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-brand-teal" />
                <a href="mailto:ckaatz50@gmail.com" className="text-base hover:text-white transition-colors" data-testid="contact-email">ckaatz50@gmail.com</a>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-brand-teal" />
                <a href="tel:+61415336952" className="text-base hover:text-white transition-colors" data-testid="contact-phone">0415 336 952</a>
              </div>
            </div>
          </div>
          <form onSubmit={submit} className="lg:col-span-7 rounded-3xl bg-white/[0.04] border border-white/10 p-6 sm:p-8 space-y-4" data-testid="enquiry-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="e-name" className="text-white/80">Your name</Label>
                <Input
                  id="e-name"
                  data-testid="enquiry-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="mt-2 bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-brand-teal"
                  placeholder="First and last"
                />
              </div>
              <div>
                <Label htmlFor="e-phone" className="text-white/80">Phone (optional)</Label>
                <Input
                  id="e-phone"
                  data-testid="enquiry-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-2 bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-brand-teal"
                  placeholder="04__"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="e-email" className="text-white/80">Email</Label>
              <Input
                id="e-email"
                type="email"
                data-testid="enquiry-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="mt-2 bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-brand-teal"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <Label htmlFor="e-msg" className="text-white/80">Tell us about your situation</Label>
              <Textarea
                id="e-msg"
                data-testid="enquiry-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                rows={5}
                className="mt-2 bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-brand-teal"
                placeholder="Where you're at, what you need help implementing…"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-brand-magenta hover:bg-brand-magentaDark text-white btn-lift h-12 px-8 text-base"
              data-testid="enquiry-submit"
            >
              {submitting ? "Sending…" : "Send enquiry"} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 bg-brand-navyDeep border-t border-white/5">
        <div className="mx-auto max-w-7xl px-5 lg:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-white/60 text-sm">
          <div>© {new Date().getFullYear()} Christine Kaatz Integrated Support Services. Registered NDIS work.</div>
          <div className="flex gap-6">
            <a href="#story" className="hover:text-white transition-colors">Story</a>
            <a href="#services" className="hover:text-white transition-colors">Services</a>
            <a href="#contact" className="hover:text-white transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ number, label }) {
  return (
    <div>
      <div className="font-display font-bold text-xl sm:text-2xl text-white">{number}</div>
      <div className="mt-1 text-xs text-white/60 leading-relaxed">{label}</div>
    </div>
  );
}
